import { Alert, MenuItem, TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { EntityFormDialog } from '../../components/dialogs/EntityFormDialog';
import { useCloneRole, useMe, useRolesList } from '../../api/queries';

const schema = z.object({
  systemRoleCode: z.string().min(1, 'Required'),
  newCode:        z.string().trim()
    .min(2, 'At least 2 characters').max(40, 'At most 40 characters')
    .regex(/^[A-Za-z][A-Za-z0-9_-]*$/, 'Letters, digits, _ and - only; must start with a letter'),
  newDisplayName: z.string().trim().min(1, 'Required').max(100, 'At most 100 characters'),
  operatorId:     z.string().uuid().optional().nullable(),
});

export type CloneRoleValues = z.infer<typeof schema>;

export function CloneRoleDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const myOperator = me.data?.operator_id ?? null;
  const clone = useCloneRole();

  const initial: CloneRoleValues = {
    systemRoleCode: 'OperatorAdmin',
    newCode:        '',
    newDisplayName: '',
    operatorId:     isArms ? null : myOperator,
  };

  return (
    <EntityFormDialog<CloneRoleValues>
      open={open}
      mode="create"
      title="Clone system role"
      submitLabel="Clone"
      schema={schema}
      initialValues={initial}
      onClose={onClose}
      onSubmit={async (values) => {
        await clone.mutateAsync({
          systemRoleCode: values.systemRoleCode,
          operatorId:     isArms ? values.operatorId ?? null : myOperator,
          newCode:        values.newCode,
          newDisplayName: values.newDisplayName,
        });
      }}
    >
      <Fields isArms={isArms} myOperator={myOperator} />
    </EntityFormDialog>
  );
}

function Fields({ isArms, myOperator }: { isArms: boolean; myOperator: string | null }) {
  const { register, formState: { errors }, watch } = useFormContext<CloneRoleValues>();
  const rolesQ = useRolesList();
  const rolesList = useQuery({ queryKey: rolesQ.queryKey, queryFn: rolesQ.listFn });
  // After #160, "templates" are tenant-less rows (operator_id == null).
  // SystemMaster is excluded because it holds every permission and cloning
  // it would mint a duplicate super-role.
  const systemRoles = (rolesList.data ?? []).filter(
    (r) => r.operatorId == null && r.code !== 'SystemMaster',
  );
  const selected = watch('systemRoleCode');

  return (
    <>
      <TextField
        select
        label="System role to clone"
        size="small"
        helperText={errors.systemRoleCode?.message ?? 'The cloned role inherits the original\'s floor permissions.'}
        error={!!errors.systemRoleCode}
        fullWidth
        value={selected ?? ''}
        {...register('systemRoleCode')}
      >
        {systemRoles.map((r) => (
          <MenuItem key={r.id} value={r.code}>
            {r.displayName} ({r.code} · {r.tier})
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="New role code"
        size="small"
        helperText={errors.newCode?.message ?? 'Letters, digits, _ and - only.'}
        error={!!errors.newCode}
        fullWidth
        autoFocus
        {...register('newCode')}
      />

      <TextField
        label="New display name"
        size="small"
        helperText={errors.newDisplayName?.message}
        error={!!errors.newDisplayName}
        fullWidth
        {...register('newDisplayName')}
      />

      {isArms && (
        <TextField
          label="Operator GUID"
          size="small"
          helperText={errors.operatorId?.message ?? 'Required — operator the new role belongs to.'}
          error={!!errors.operatorId}
          fullWidth
          {...register('operatorId')}
        />
      )}

      {!isArms && (
        <Alert severity="info" variant="outlined">
          Clone will be owned by your operator: <code>{myOperator}</code>
        </Alert>
      )}
    </>
  );
}
